import React, {memo, useMemo} from "react"
import {createSearchParams, useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {format, parseISO} from "date-fns";
import {
    useReactTable,
    getCoreRowModel,
} from '@tanstack/react-table'

import DataTable from "../../../components/DataTable";

import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Icon from "@mui/material/Icon";

import Filters from "./Filters";
import Tooltip from "@mui/material/Tooltip";
import StationCode from "../../../components/columns/StationCode";
import useDialog from "../../../components/useDialog";
import DownloadDialog from "./DownloadDialog";
import {useModel23RegistryQuery} from "../query";


export const statusIcons = {
    "OPEN": <Icon fontSize="small" color="warning">edit_document</Icon>,
    "CLOSED": <Icon fontSize="small" color="info">check</Icon>,
    "CONFIRMED": <Icon fontSize="small" color="success">lock</Icon>,
}


const cellWidth = "2rem";


const formatAspect = (aspect) => {
    if(aspect){
        return aspect.split("_").map(w => w[0]).join("")
    }else{
        return ""
    }
}

const formatSwe = (swe) => {
    return isNaN(swe) || swe === null || swe === undefined ? "" : (swe?.toFixed(1) ?? '');
}

const Models23Table = (props) => {
    const {
        rows,
        page_count = -1,
        pagination,
        setPagination,
        onGlobalFilterChange,
        onFilterChange,
        sorting,
        onSortingChange,
        multipleEditing,
        loading,
        filterProps,
        fetchDataOptions,
        map,
    } = props;
    const {t} = useTranslation(["models23", "enums"]);
    const model23RegistryQuery = useModel23RegistryQuery();
    const model23Registry = model23RegistryQuery.data;

    const columns = useMemo(
        () => [
            {
                accessorKey: 'stationCode',  // this is the key used in filter and sorting
                cell: props => <StationCode station={props?.row?.original?.station} align="left"/>,
                header: () => <span>{t("table.station")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "left",
                    width: "5rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'stationType',  // this is the key used in filter and sorting
                cell: props => props?.row?.original?.station?.type === "ITINERANT" && <Icon fontSize="small">hiking</Icon>,
                header: () => null,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "3rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'locationName',
                cell: info => info.getValue(),
                header: () => <span>{t("table.locationName")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    width: "10rem",
                    px: 0,
                    py: 0
                }
            },
            {
                accessorKey: 'status',
                cell: cell => <StatusCol key={`${cell.row?.original?.id}_${cell.cell.column.id}`}
                                         cell={cell}/>,
                header: () => <span>{t("table.status")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "4rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'datetime',
                cell: props => format(parseISO(props.row?.original?.datetime), "dd/MM/yyyy HH:mm"),
                header: () => <span>{t("table.datetime")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "9rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'elevation',
                cell: props => props.getValue() !== null && `${props.getValue()} mt.`,
                header: () => <span>{t("table.elevation")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "6rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'aspect',
                cell: props => formatAspect(props.getValue()),
                header: () => <span>{t("table.aspect")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "5rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'slope',
                cell: props => props.getValue(),
                header: () => <span>{t("table.slope")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "5rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            ...["ww", "n", "v", "vq1", "vq2", "ta", "hs"].map(param => {
                return {
                    accessorKey: param,
                    cell: props => props.row?.original?.snowWeatherData[param] ?? "",
                    header: () => param,
                    enableSorting: false,
                    footer: props => props.column.id,
                    meta: {
                        align: "center",
                        width: cellWidth,
                        px: "1px",
                        vAlign: "top",
                        headerTooltip: t(`table.${param}`)
                    }
                }
            }),
            {
                accessorKey: "swe",
                cell: props => <SweCell cell={props} />,
                header: () => "SWE",
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "5rem",
                    px: "1px",
                    vAlign: "top",
                    headerTooltip: t('table.swe')
                }
            },
            {
                accessorKey: 'elementCodes',
                cell: props => <ElementsCell cell={props} registry={model23Registry}/>,
                header: () => <span>{t("table.elements")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "left",
                    width: "6rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'notes',
                cell: props => <NoteCell cell={props}></NoteCell>,
                header: () => <span>{t("table.notes")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "left",
                    px: 2,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'feedback',
                cell: props => <NoteCell cell={props}></NoteCell>,
                header: () => <span>{t("table.feedback")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    align: "left",
                    width: "5rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'user',
                cell: props => <UserCell cell={props}/>,
                header: () => <span>{t("table.user")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "center",
                    width: "4rem",
                    px: 0,
                    vAlign: "top"
                }
            },
            {
                accessorKey: 'Actions',
                cell: props => <ActionCol cell={props}/>,
                header: () => <span>{t("table.actions")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "right",
                    width: "8rem",
                    px: 0,
                    vAlign: "top"
                }
            },
        ],
        [model23Registry]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    // Re-render happen every time the data/rows change.
    // We re-create a new array because we want to control when the table is re-rendered,
    // and it should be re-rendered when either rows change or multipleEditing changes
    const data = useMemo(() => rows ? [...rows] : [], [rows, multipleEditing])

    const table = useReactTable({
        data: data,
        columns,
        pageCount: page_count ?? -1,
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onGlobalFilterChange: onGlobalFilterChange,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,  // rows are expected to be paginated (controlled mode)
        manualSorting: true,  // rows are expected to be sorted (controlled mode)
        // getPaginationRowModel: getPaginationRowModel(), // If only doing manual pagination, you don't need this
        debugTable: true,
        meta: {
            fetchDataOptions,
            map,
        }
    })
    return <DataTable dataTableId={props.dataTableId} table={table}
                      canSearch
                      searchPlaceholder={t("table.searchStation")}
                      showRowColoring
                      rowColoring={props.rowColoring}
                      setRowColoring={props.setRowColoring}
                      stickyHeader={true}
                      filtersEl={<Filters onChange={onFilterChange}
                                          loading={loading}
                                          {...filterProps}/>}/>
}

const areEqualCellValues = (prevProps, nextProps) => {
    const prevValue = prevProps.cell.getValue();
    const nextValue = nextProps.cell.getValue();
    return prevValue === nextValue;
}

const areEqualActionCol = (prevProps, nextProps) => {
    return prevProps.cell.row.original.id === nextProps.cell.row.original.id && prevProps.cell.row.original.updatedAt === nextProps.cell.row.original.updatedAt;
}

const StatusCol = memo(({cell}) => {
    const status = cell.getValue();

    if (statusIcons[status]) {
        return statusIcons[status];
    }
    return null
}, areEqualCellValues)

const NoteCell = memo(({cell}) => {
    const value = cell.getValue();
    if (value !== "" && value != null) {
        return <Tooltip title={value} placement="bottom" disableInteractive>
            <span>{value.length > 16 ? value.substring(0, 15) + "..." : value}</span>
        </Tooltip>
    } else {
        return null;
    }
}, areEqualCellValues)


const ElementsCell = memo(({cell, registry}) => {
    const element_codes = cell.getValue() ?? [];
    const summary = element_codes.join(" ")
    const elementCodeRegistry = registry?.elementCodeRegistry || [];

    const flagsToDescription = {};
    elementCodeRegistry.forEach(item => {
        flagsToDescription[item.code] = item.description;
    });
    const activeElements = element_codes.map( elementCode => flagsToDescription[elementCode] ?? elementCode);

    if (summary !== "") {
        const content = activeElements.join("\n");
        return <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{content}</div>} placement="bottom" disableInteractive>
            <span>{summary.length > 12 ? summary.substring(0, 9) + "..." : summary}</span>
        </Tooltip>
    } else {
        return null;
    }
}, (prevProps, nextProps) => {
    return areEqualCellValues(prevProps, nextProps) && prevProps.registry === nextProps.registry;
})


const ActionCol = memo(({cell}) => {
    const navigate = useNavigate();
    const model23 = cell.row.original;
    const {id} = model23;
    const map = cell.table.options?.meta.map;
    const pos = {lat: model23.geom.coordinates[1], lng: model23.geom.coordinates[0]}
    const fetchDataOptions = cell.table?.options?.meta?.fetchDataOptions;
    const downloadDialog = useDialog();

    return <Stack direction="row" spacing={0.5} justifyContent="end">
        <IconButton onClick={() => navigate({pathname: '/models23/models4', search: createSearchParams({ selectedModel23Id: id, ...fetchDataOptions }).toString()})}
                    sx={{p: 0}}>
            <Icon fontSize="small" sx={{transform: "rotate(270deg)"}}>bar_chart_4_bars</Icon>
        </IconButton>
        <IconButton onClick={downloadDialog.open} sx={{p: 0}}>
            <Icon fontSize="small">file_save</Icon>
        </IconButton>
        <IconButton onClick={() => map.flyTo(pos)} sx={{p: 0}}>
            <Icon fontSize="small">location_searching</Icon>
        </IconButton>
        <IconButton onClick={() => navigate(`/models23/${id}/edit`)} sx={{p: 0}}>
            <Icon fontSize="small">edit</Icon>
        </IconButton>
        {
            downloadDialog.isOpen && <DownloadDialog onClose={downloadDialog.close} model23={model23} />
        }
    </Stack>
}, areEqualActionCol)


const SweCell = memo(({cell}) => {
    const {t} = useTranslation(["models23"]);
    const swe = cell.row?.original?.swe;
    const sweComputed = cell.row?.original?.sweComputed;
    const sweEstimated = cell.row?.original?.sweEstimated;

    const value = `${formatSwe(swe)}${swe !== sweComputed ? ' *' : ''}`;
    const content = `swe: ${formatSwe(swe)}\n${t('table.sweComputed')}: ${formatSwe(sweComputed)}\n${t('table.sweEstimated')}: ${formatSwe(sweEstimated)}`;

    if(swe !== null && swe !== undefined){
        return <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{content}</div>} placement="bottom" disableInteractive>
            <span>{value}</span>
        </Tooltip>
    } else {
        return null;
    }
}, areEqualCellValues)


const UserCell = memo(({cell}) => {
    const user = cell.row.original.user;
    const fullName = user ? `${user?.surname} ${user.name}` : "";

    if (fullName !== "" && fullName != null) {
        return <Tooltip title={fullName} placement="bottom" disableInteractive>
            <span>{fullName.length > 8 ? fullName.substring(0, 7) + "..." : fullName}</span>
        </Tooltip>
    } else {
        return null;
    }
}, (prevProps, nextProps) => {
    return prevProps.cell.row.original.user?.id === nextProps.cell.row.original.user?.id;
});


export default Models23Table;